import { getWalletAddress, getWalletProviderId } from '@/utils/auth-token';
import { getPrivyEmbeddedProvider } from '@/utils/privy';
import {
  getMetaMaskProvider,
  getOkxProvider,
  getCoinbaseProvider,
  getBitgetProvider,
} from '@/utils/login';
import {
  ensureWalletConnectSigningProvider,
  getWalletConnectProvider,
  hasWalletConnectSession,
  isWalletConnectSessionActive,
} from '@/utils/wallet-connect-session';

/** 与 AuthDialog / AuthWalletIcon 保持一致的可识别钱包 id */
export const KNOWN_WALLET_PROVIDER_IDS = [
  'privy',
  'metamask',
  'tronlink',
  'coinbase',
  'okx',
  'bitget',
  'phantom',
  'walletconnect',
];

const KNOWN_WALLET_ID_SET = new Set(KNOWN_WALLET_PROVIDER_IDS);

/** 无法通过 EVM eth_accounts 校验的钱包，UI 直接沿用登录缓存 */
const DISPLAY_ONLY_WALLET_IDS = new Set(['privy', 'tronlink', 'phantom']);

const PROVIDER_GETTERS = {
  metamask: getMetaMaskProvider,
  okx: getOkxProvider,
  coinbase: getCoinbaseProvider,
  bitget: getBitgetProvider,
  walletconnect: getWalletConnectProvider,
};

const DETECT_ORDER = ['okx', 'metamask', 'coinbase', 'bitget'];

/** @returns {string} i18n key suffix under authDialog.wallets.* */
export function normalizeWalletProviderId(id) {
  const value = String(id ?? '')
    .trim()
    .toLowerCase();
  if (value && KNOWN_WALLET_ID_SET.has(value)) return value;
  return 'metamask';
}

export function isKnownWalletProviderId(id) {
  return KNOWN_WALLET_ID_SET.has(normalizeWalletProviderId(id));
}

export function getWalletProviderById(id) {
  const normalized = normalizeWalletProviderId(id);
  const getter = PROVIDER_GETTERS[normalized];
  return getter ? getter() : null;
}

/**
 * 按用户登录时保存的钱包类型取 signing provider；无缓存时回退到探测顺序。
 */
export async function getWalletSigningProvider(preferredId) {
  const stored = String(preferredId ?? getWalletProviderId() ?? '')
    .trim()
    .toLowerCase();

  if (stored === 'privy') {
    const privyProvider = await getPrivyEmbeddedProvider();
    if (privyProvider) return privyProvider;
  }

  if (stored === 'walletconnect') {
    const wcProvider = await ensureWalletConnectSigningProvider();
    if (wcProvider) return wcProvider;
    return null;
  }

  if (stored && PROVIDER_GETTERS[stored]) {
    const preferred = PROVIDER_GETTERS[stored]();
    if (preferred) return preferred;
  }

  for (const id of DETECT_ORDER) {
    const provider = PROVIDER_GETTERS[id]?.();
    if (provider) return provider;
  }

  if (typeof window !== 'undefined' && window.ethereum) {
    return window.ethereum;
  }
  return null;
}

async function providerOwnsAddress(provider, address) {
  if (!provider?.request || !address) return false;
  try {
    const accounts = await provider.request({ method: 'eth_accounts' });
    const target = String(address).trim().toLowerCase();
    return (accounts || []).some((item) => String(item).trim().toLowerCase() === target);
  } catch {
    return false;
  }
}

/**
 * 解析 UI 展示用的钱包类型：按地址匹配 MetaMask/OKX 等，Privy 登录时也可识别外部钱包。
 */
export async function resolveConnectedWalletProviderId(address) {
  const normalizedAddress = String(address ?? getWalletAddress() ?? '').trim();

  for (const id of DETECT_ORDER) {
    const provider = PROVIDER_GETTERS[id]?.();
    if (await providerOwnsAddress(provider, normalizedAddress)) {
      return id;
    }
  }

  const rawStored = String(getWalletProviderId() ?? '')
    .trim()
    .toLowerCase();
  const stored = normalizeWalletProviderId(rawStored);

  if (rawStored === 'walletconnect') {
    const wcProvider = getWalletConnectProvider();
    if (wcProvider && (await providerOwnsAddress(wcProvider, normalizedAddress))) {
      return 'walletconnect';
    }
    if (hasWalletConnectSession() || isWalletConnectSessionActive()) {
      return 'walletconnect';
    }
  }

  if (rawStored && DISPLAY_ONLY_WALLET_IDS.has(rawStored)) {
    return stored;
  }

  if (normalizedAddress && PROVIDER_GETTERS[stored]) {
    const storedProvider = PROVIDER_GETTERS[stored]();
    if (await providerOwnsAddress(storedProvider, normalizedAddress)) {
      return stored;
    }
  }

  return stored;
}
